/**
 * MedPass Dashboard Component
 * 
 * Main dashboard for managing MedPass workflows, including listing,
 * filtering, creating, and monitoring electronic prescription workflows.
 */

import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Table,
    Button,
    Input,
    Select,
    DatePicker,
    Space,
    Tag,
    Progress,
    Badge,
    Modal,
    message,
    Tooltip,
    Statistic,
    Alert,
    Drawer
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    WarningOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import moment from 'moment';

import medpassService from '../../services/medpassService';
import MedPassWorkflow from './MedPassWorkflow';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

const MedPassDashboard = () => {
    // State management
    const [loading, setLoading] = useState(false);
    const [workflows, setWorkflows] = useState([]);
    const [filteredWorkflows, setFilteredWorkflows] = useState([]);
    const [stats, setStats] = useState(null);
    const [filters, setFilters] = useState({
        status: null,
        step: null,
        patientSearch: '',
        dateRange: null,
        requiresApproval: null,
        hasSafetyAlerts: null
    });
    const [showWorkflowModal, setShowWorkflowModal] = useState(false);
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [workflowMode, setWorkflowMode] = useState('create');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0
    });

    // Load data on mount
    useEffect(() => {
        loadWorkflows();
        loadStats();
    }, []);

    // Apply filters when they change
    useEffect(() => {
        applyFilters();
    }, [workflows, filters]);

    const loadWorkflows = async (params = {}) => {
        setLoading(true);
        try {
            const searchParams = {
                skip: (pagination.current - 1) * pagination.pageSize,
                limit: pagination.pageSize,
                ...params
            };

            const result = await medpassService.listWorkflows(searchParams);
            if (result.success) {
                setWorkflows(result.data);
                setPagination(prev => ({
                    ...prev,
                    total: result.data.length // This would come from API response
                }));
            } else {
                message.error(result.message);
            }
        } catch (error) {
            message.error('Failed to load workflows');
            console.error('Error loading workflows:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const result = await medpassService.getWorkflowStats();
            if (result.success) {
                setStats(result.data);
            }
        } catch (error) {
            console.error('Error loading stats:', error);
        }
    };

    const applyFilters = () => {
        let filtered = [...workflows];

        // Status filter
        if (filters.status) {
            filtered = filtered.filter(w => w.status === filters.status);
        }

        // Step filter
        if (filters.step) {
            filtered = filtered.filter(w => w.current_step === filters.step);
        }

        // Patient search filter
        if (filters.patientSearch) {
            const search = filters.patientSearch.toLowerCase();
            filtered = filtered.filter(w => 
                w.patient_name?.toLowerCase().includes(search) ||
                w.patient_mrn?.toLowerCase().includes(search)
            );
        }

        // Date range filter
        if (filters.dateRange && filters.dateRange.length === 2) {
            const [start, end] = filters.dateRange;
            filtered = filtered.filter(w => {
                const createdAt = moment(w.created_at);
                return createdAt.isBetween(start, end, 'day', '[]');
            });
        }

        // Approval filter
        if (filters.requiresApproval !== null) {
            filtered = filtered.filter(w => w.requires_approval === filters.requiresApproval);
        }

        // Safety alerts filter
        if (filters.hasSafetyAlerts !== null) {
            filtered = filtered.filter(w => w.has_safety_alerts === filters.hasSafetyAlerts);
        }

        setFilteredWorkflows(filtered);
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const createNewWorkflow = () => {
        setSelectedWorkflow(null);
        setWorkflowMode('create');
        setShowWorkflowModal(true);
    };

    const viewWorkflow = (workflow) => {
        setSelectedWorkflow(workflow.workflow_id);
        setWorkflowMode('view');
        setShowWorkflowModal(true);
    };

    const editWorkflow = (workflow) => {
        setSelectedWorkflow(workflow.workflow_id);
        setWorkflowMode('edit');
        setShowWorkflowModal(true);
    };

    const deleteWorkflow = async (workflowId) => {
        try {
            const result = await medpassService.deleteWorkflow(workflowId);
            if (result.success) {
                message.success('Workflow deleted successfully');
                loadWorkflows();
            } else {
                message.error(result.message);
            }
        } catch (error) {
            message.error('Failed to delete workflow');
        }
    };

    const handleWorkflowComplete = (workflow) => {
        setShowWorkflowModal(false);
        loadWorkflows();
        loadStats();
        message.success('Workflow completed successfully');
    };

    // Get step names for display
    const stepNames = medpassService.getWorkflowStepNames();
    const statusNames = medpassService.getWorkflowStatusNames();

    // Table columns
    const columns = [
        {
            title: 'Patient',
            key: 'patient',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>
                        {record.patient_name || 'Unknown Patient'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        MRN: {record.patient_mrn || 'Unknown'}
                    </div>
                </div>
            )
        },
        {
            title: 'Current Step',
            dataIndex: 'current_step',
            key: 'current_step',
            render: (step) => (
                <Tag color="blue">
                    {stepNames[step] || step}
                </Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = {
                    'draft': 'default',
                    'in_progress': 'processing',
                    'pending_review': 'warning',
                    'approved': 'success',
                    'completed': 'success',
                    'cancelled': 'error',
                    'rejected': 'error'
                };
                return (
                    <Tag color={colors[status] || 'default'}>
                        {statusNames[status] || status}
                    </Tag>
                );
            }
        },
        {
            title: 'Progress',
            dataIndex: 'progress_percentage',
            key: 'progress',
            render: (progress) => (
                <Progress 
                    percent={progress || 0} 
                    size="small" 
                    status={progress === 100 ? 'success' : 'active'}
                />
            )
        },
        {
            title: 'Medications',
            dataIndex: 'medication_count',
            key: 'medication_count',
            render: (count) => (
                <Badge count={count || 0} style={{ backgroundColor: '#52c41a' }} />
            )
        },
        {
            title: 'Alerts',
            key: 'alerts',
            render: (_, record) => (
                <Space>
                    {record.has_safety_alerts && (
                        <Tooltip title="Has safety alerts">
                            <Badge status="warning" />
                        </Tooltip>
                    )}
                    {record.has_validation_errors && (
                        <Tooltip title="Has validation errors">
                            <Badge status="error" />
                        </Tooltip>
                    )}
                    {record.requires_approval && (
                        <Tooltip title="Requires approval">
                            <Badge status="processing" />
                        </Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => moment(date).format('DD/MM/YYYY hh:mm A')
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View">
                        <Button 
                            type="text" 
                            icon={<EyeOutlined />}
                            onClick={() => viewWorkflow(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button 
                            type="text" 
                            icon={<EditOutlined />}
                            onClick={() => editWorkflow(record)}
                            size="small"
                            disabled={record.status === 'completed' || record.status === 'approved'}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button 
                            type="text" 
                            danger 
                            icon={<DeleteOutlined />}
                            onClick={() => {
                                Modal.confirm({
                                    title: 'Delete Workflow',
                                    content: 'Are you sure you want to delete this workflow?',
                                    onOk: () => deleteWorkflow(record.workflow_id)
                                });
                            }}
                            size="small"
                            disabled={record.status === 'approved' || record.status === 'completed'}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    const renderStats = () => {
        if (!stats) return null;

        return (
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Total Workflows"
                            value={stats.total_workflows}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Pending Approval"
                            value={stats.pending_approval}
                            prefix={<ExclamationCircleOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="With Safety Alerts"
                            value={stats.with_safety_alerts}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: '#ff4d4f' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Completed Today"
                            value={stats.completed_today}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>
        );
    };

    const renderFilters = () => (
        <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={12} md={6}>
                    <Search
                        placeholder="Search patient name or MRN"
                        allowClear
                        onSearch={(value) => handleFilterChange('patientSearch', value)}
                        onChange={(e) => handleFilterChange('patientSearch', e.target.value)}
                    />
                </Col>
                
                <Col xs={24} sm={12} md={4}>
                    <Select
                        placeholder="Status"
                        allowClear
                        style={{ width: '100%' }}
                        onChange={(value) => handleFilterChange('status', value)}
                    >
                        {Object.entries(statusNames).map(([key, label]) => (
                            <Option key={key} value={key}>{label}</Option>
                        ))}
                    </Select>
                </Col>
                
                <Col xs={24} sm={12} md={4}>
                    <Select
                        placeholder="Current Step"
                        allowClear
                        style={{ width: '100%' }}
                        onChange={(value) => handleFilterChange('step', value)}
                    >
                        {Object.entries(stepNames).map(([key, label]) => (
                            <Option key={key} value={key}>{label}</Option>
                        ))}
                    </Select>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                    <RangePicker
                        style={{ width: '100%' }}
                        onChange={(dates) => handleFilterChange('dateRange', dates)}
                    />
                </Col>
                
                <Col xs={24} sm={12} md={4}>
                    <Space>
                        <Button 
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                setFilters({
                                    status: null,
                                    step: null,
                                    patientSearch: '',
                                    dateRange: null,
                                    requiresApproval: null,
                                    hasSafetyAlerts: null
                                });
                                loadWorkflows();
                            }}
                        >
                            Reset
                        </Button>
                    </Space>
                </Col>
            </Row>
        </Card>
    );

    return (
        <div className="medpass-dashboard">
            {/* Page Header */}
            <Card style={{ marginBottom: 16 }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <h2 style={{ margin: 0 }}>MedPass Electronic Prescription Workflows</h2>
                        <p style={{ margin: 0, color: '#666' }}>
                            Manage 4-step electronic prescription workflows with safety validation
                        </p>
                    </Col>
                    <Col>
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />}
                            onClick={createNewWorkflow}
                            size="large"
                        >
                            New MedPass Workflow
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Statistics */}
            {renderStats()}

            {/* Filters */}
            {renderFilters()}

            {/* Quick Actions */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col>
                    <Button 
                        type="dashed"
                        onClick={() => handleFilterChange('requiresApproval', true)}
                    >
                        Pending Approval ({stats?.pending_approval || 0})
                    </Button>
                </Col>
                <Col>
                    <Button 
                        type="dashed"
                        onClick={() => handleFilterChange('hasSafetyAlerts', true)}
                    >
                        Safety Alerts ({stats?.with_safety_alerts || 0})
                    </Button>
                </Col>
                <Col>
                    <Button 
                        type="dashed"
                        onClick={() => handleFilterChange('status', 'in_progress')}
                    >
                        In Progress
                    </Button>
                </Col>
            </Row>

            {/* Workflows Table */}
            <Card>
                <Table
                    dataSource={filteredWorkflows}
                    columns={columns}
                    rowKey="workflow_id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        onChange: (page, pageSize) => {
                            setPagination(prev => ({
                                ...prev,
                                current: page,
                                pageSize
                            }));
                            loadWorkflows({ 
                                skip: (page - 1) * pageSize,
                                limit: pageSize 
                            });
                        }
                    }}
                    scroll={{ x: 1200 }}
                />
            </Card>

            {/* Workflow Modal */}
            <Modal
                title={
                    workflowMode === 'create' 
                        ? 'New MedPass Workflow'
                        : workflowMode === 'edit'
                            ? 'Edit MedPass Workflow'
                            : 'View MedPass Workflow'
                }
                open={showWorkflowModal}
                onCancel={() => setShowWorkflowModal(false)}
                footer={null}
                width="90%"
                style={{ top: 20 }}
                destroyOnClose
            >
                <MedPassWorkflow
                    workflowId={selectedWorkflow}
                    mode={workflowMode}
                    onWorkflowComplete={handleWorkflowComplete}
                    onCancel={() => setShowWorkflowModal(false)}
                />
            </Modal>
        </div>
    );
};

export default MedPassDashboard;